/**
 * publication.ts - Tournament publishing and sharing features
 */

import { updateTournament as updateTournamentAPI } from '../api/tournament-api';
import { t } from '../utils/i18n-helper';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

// Track sections that are temporarily locked from toggling
export const toggleLocks = new Map<string, number>();

/**
 * Toggle feature section visibility
 */
export function toggleFeatureSection(tournamentId: string, sectionType: 'publishing' | 'fees' | 'details' | 'keyinfo' | 'settings', event?: Event): void {
  const section = document.getElementById(`${sectionType}-section-${tournamentId}`);
  const icon = document.getElementById(`${sectionType}-icon-${tournamentId}`);
  const button = event ? (event.target as HTMLElement)?.closest('.feature-section-header') as HTMLElement : null;

  if (!section) return;

  // Check if this section is temporarily locked from toggling
  const lockKey = `${tournamentId}-${sectionType}`;
  if (toggleLocks.has(lockKey) && Date.now() < toggleLocks.get(lockKey)!) {
    return;
  }

  // Don't toggle if there's an active edit in progress (prevents accidental collapse while editing)
  const hasActiveEdit = section.querySelector('.editing');
  if (hasActiveEdit) {
    return;
  }

  const isHidden = section.style.display === 'none';
  section.style.display = isHidden ? 'block' : 'none';

  if (icon) {
    icon.textContent = isHidden ? '▼' : '▶';
  }

  if (button) {
    if (isHidden) {
      button.classList.add('expanded');
    } else {
      button.classList.remove('expanded');
    }
  }

  // Save/clear the session storage flag for restoring state after reload
  if (isHidden) {
    // Section is now being opened, save the state
    sessionStorage.setItem(`${sectionType}-section-${tournamentId}-open`, 'true');

    // Initialize tiebreak drag-drop and categories when settings section is opened
    if (sectionType === 'settings') {
      // Small delay to ensure DOM is ready
      if (window.initTiebreakDragDrop) {
        setTimeout(() => window.initTiebreakDragDrop(tournamentId), 50);
      }
      // Load categories (now part of settings section)
      if (window.initCategoriesSection) {
        setTimeout(() => window.initCategoriesSection(tournamentId), 50);
      }
    }
  } else {
    // Section is being closed
    sessionStorage.removeItem(`${sectionType}-section-${tournamentId}-open`);
  }
}

/**
 * Auto-open sections that should be open (called after page load)
 */
export function autoOpenSections(tournamentId: string): void {
  // List of all section types to check (categories is now part of settings)
  const sectionTypes = ['keyinfo', 'settings', 'publishing', 'fees'];

  // Clean up old categories session storage key
  sessionStorage.removeItem(`categories-section-${tournamentId}-open`);

  for (const sectionType of sectionTypes) {
    if (sessionStorage.getItem(`${sectionType}-section-${tournamentId}-open`) === 'true') {
      const section = document.getElementById(`${sectionType}-section-${tournamentId}`);
      const icon = document.getElementById(`${sectionType}-icon-${tournamentId}`);
      const button = document.querySelector(`#${sectionType}-section-${tournamentId}`)?.closest('.feature-section')?.querySelector('.feature-section-header') as HTMLElement;

      if (section) {
        section.style.display = 'block';
        if (icon) icon.textContent = '▼';
        if (button) button.classList.add('expanded');

        // Initialize tiebreak drag-drop and categories when settings section is auto-opened
        if (sectionType === 'settings') {
          if (window.initTiebreakDragDrop) {
            setTimeout(() => window.initTiebreakDragDrop(tournamentId), 50);
          }
          if (window.initCategoriesSection) {
            setTimeout(() => window.initCategoriesSection(tournamentId), 50);
          }
        }
      }

      // Don't remove the flag - keep it so sections stay open on subsequent reloads
      // The flag will be removed when the user explicitly closes the section
    }
  }
}

/**
 * Toggle self-registration
 */
export async function toggleSelfRegistration(tournamentId: string, enabled: boolean): Promise<void> {
  try {
    await updateTournamentAPI(tournamentId, { allowSelfRegistration: enabled });
    // No UI update needed - checkbox state is already updated by the user's click
  } catch (error) {
    console.error('Failed to toggle self-registration:', error);
    alert(t('common.error', 'Error') + ': ' + (error as Error).message);

    // Reset checkbox state on error
    const checkbox = document.getElementById(`allow-self-reg-${tournamentId}`) as HTMLInputElement;
    if (checkbox) {
      checkbox.checked = !enabled;
    }
  }
}

/**
 * Toggle player result entry
 */
export async function togglePlayerResultEntry(tournamentId: string, enabled: boolean): Promise<void> {
  try {
    await updateTournamentAPI(tournamentId, { allowUserResultEntry: enabled });
    // No UI update needed - checkbox state is already updated by the user's click
  } catch (error) {
    console.error('Failed to toggle player result entry:', error);
    alert(t('common.error', 'Error') + ': ' + (error as Error).message);

    // Reset checkbox state on error
    const checkbox = document.getElementById(`allow-player-results-${tournamentId}`) as HTMLInputElement;
    if (checkbox) {
      checkbox.checked = !enabled;
    }
  }
}

/**
 * Toggle shareable link visibility
 */
export async function toggleShareLinkVisibility(tournamentId: string, show: boolean): Promise<void> {
  try {
    if (show) {
      // Generate token if it doesn't exist yet
      const response = await fetch(`/api/tournaments/${tournamentId}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to generate share token');
      }

      const data = await response.json();

      // Update UI with the new share token
      const section = document.getElementById(`share-link-section-${tournamentId}`);
      if (section) {
        section.style.display = 'block';

        const linkInput = document.getElementById(`share-link-input-${tournamentId}`) as HTMLInputElement;
        if (linkInput && data.shareToken) {
          linkInput.value = `${window.location.origin}/share/${data.shareToken}`;
        }

        const viewBtn = document.getElementById(`view-tournament-btn-${tournamentId}`);
        if (viewBtn && data.shareToken) {
          viewBtn.setAttribute('onclick', `window.open('${window.location.origin}/share/${data.shareToken}', '_blank')`);
        }
      }

      // Show self-registration option if not already visible
      const selfRegLabel = document.getElementById(`allow-self-reg-${tournamentId}`)?.closest('.feature-label') as HTMLElement;
      if (selfRegLabel && selfRegLabel.style.display === 'none') {
        selfRegLabel.style.display = 'flex';
      }

      // Show player result entry option if not already visible
      const playerResultsLabel = document.getElementById(`allow-player-results-${tournamentId}`)?.closest('.feature-label') as HTMLElement;
      if (playerResultsLabel && playerResultsLabel.style.display === 'none') {
        playerResultsLabel.style.display = 'flex';
      }
    } else {
      // Remove the share token
      await updateTournamentAPI(tournamentId, { shareToken: null });

      // Hide the section
      const section = document.getElementById(`share-link-section-${tournamentId}`);
      if (section) {
        section.style.display = 'none';
      }

      // Check if public directory is also disabled - if so, hide self-registration and player results and disable them
      const publishCheckbox = document.getElementById(`publish-directory-${tournamentId}`) as HTMLInputElement;
      const selfRegCheckbox = document.getElementById(`allow-self-reg-${tournamentId}`) as HTMLInputElement;
      const selfRegLabel = selfRegCheckbox?.closest('.feature-label') as HTMLElement;
      const playerResultsCheckbox = document.getElementById(`allow-player-results-${tournamentId}`) as HTMLInputElement;
      const playerResultsLabel = playerResultsCheckbox?.closest('.feature-label') as HTMLElement;

      if (!publishCheckbox?.checked) {
        // Both sharing methods are disabled, so disable and hide self-registration
        if (selfRegLabel) {
          if (selfRegCheckbox && selfRegCheckbox.checked) {
            selfRegCheckbox.checked = false;
            await updateTournamentAPI(tournamentId, { allowSelfRegistration: false });
          }
          selfRegLabel.style.display = 'none';
        }

        // Also disable and hide player result entry
        if (playerResultsLabel) {
          if (playerResultsCheckbox && playerResultsCheckbox.checked) {
            playerResultsCheckbox.checked = false;
            await updateTournamentAPI(tournamentId, { allowUserResultEntry: false });
          }
          playerResultsLabel.style.display = 'none';
        }
      }
    }
  } catch (error) {
    console.error('Failed to toggle share link:', error);
    alert(t('common.error', 'Error') + ': ' + (error as Error).message);

    // Reload single tournament to reset state
    if (window.loadSingleTournament) {
      await window.loadSingleTournament(tournamentId);
    }
  }
}

/**
 * Toggle public directory publishing on/off
 */
export async function togglePublicDirectory(tournamentId: string, enabled: boolean): Promise<void> {
  try {
    if (enabled) {
      // Publish to public directory (generates share token if needed)
      const response = await fetch(`/api/tournaments/${tournamentId}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.error
          ? t(errorData.error, 'Failed to publish tournament')
          : 'Failed to publish tournament';
        throw new Error(errorMessage);
      }

      // Show "View in Public Directory" button
      const publishCheckbox = document.getElementById(`publish-directory-${tournamentId}`);
      if (publishCheckbox) {
        const label = publishCheckbox.closest('.feature-label');
        if (label) {
          // Check if button already exists
          let viewDirBtn = label.nextElementSibling as HTMLElement;
          if (!viewDirBtn || !viewDirBtn.querySelector('.btn-secondary')) {
            // Create and insert the button
            const btnDiv = document.createElement('div');
            btnDiv.style.cssText = 'margin: 8px 0 0 24px;';
            btnDiv.innerHTML = `<button class="btn-secondary btn-small" onclick="window.open('/tournaments.html', '_blank')">${t('tournament.viewInDirectory', 'View in Public Directory')}</button>`;
            label.insertAdjacentElement('afterend', btnDiv);
          }
        }
      }

      // Show self-registration option if not already visible
      const selfRegLabel = document.getElementById(`allow-self-reg-${tournamentId}`)?.closest('.feature-label') as HTMLElement;
      if (selfRegLabel && selfRegLabel.style.display === 'none') {
        selfRegLabel.style.display = 'flex';
      }

      // Show player result entry option if not already visible
      const playerResultsLabel = document.getElementById(`allow-player-results-${tournamentId}`)?.closest('.feature-label') as HTMLElement;
      if (playerResultsLabel && playerResultsLabel.style.display === 'none') {
        playerResultsLabel.style.display = 'flex';
      }
    } else {
      // Unpublish from directory (but keep share token)
      await updateTournamentAPI(tournamentId, { published: false });

      // Hide "View in Public Directory" button
      const publishCheckbox = document.getElementById(`publish-directory-${tournamentId}`);
      if (publishCheckbox) {
        const label = publishCheckbox.closest('.feature-label');
        if (label) {
          const btnDiv = label.nextElementSibling as HTMLElement;
          if (btnDiv && btnDiv.querySelector('.btn-secondary')) {
            btnDiv.remove();
          }
        }
      }

      // Check if shareable link is also disabled - if so, hide self-registration and player results and disable them
      const shareLinkCheckbox = document.getElementById(`enable-share-link-${tournamentId}`) as HTMLInputElement;
      const selfRegCheckbox = document.getElementById(`allow-self-reg-${tournamentId}`) as HTMLInputElement;
      const selfRegLabel = selfRegCheckbox?.closest('.feature-label') as HTMLElement;
      const playerResultsCheckbox = document.getElementById(`allow-player-results-${tournamentId}`) as HTMLInputElement;
      const playerResultsLabel = playerResultsCheckbox?.closest('.feature-label') as HTMLElement;

      if (!shareLinkCheckbox?.checked) {
        // Both sharing methods are disabled, so disable and hide self-registration
        if (selfRegLabel) {
          if (selfRegCheckbox && selfRegCheckbox.checked) {
            selfRegCheckbox.checked = false;
            await updateTournamentAPI(tournamentId, { allowSelfRegistration: false });
          }
          selfRegLabel.style.display = 'none';
        }

        // Also disable and hide player result entry
        if (playerResultsLabel) {
          if (playerResultsCheckbox && playerResultsCheckbox.checked) {
            playerResultsCheckbox.checked = false;
            await updateTournamentAPI(tournamentId, { allowUserResultEntry: false });
          }
          playerResultsLabel.style.display = 'none';
        }
      }
    }
  } catch (error) {
    console.error('Failed to toggle public directory:', error);
    alert(t('common.error', 'Error') + ': ' + (error as Error).message);

    // Reload single tournament to reset state
    if (window.loadSingleTournament) {
      await window.loadSingleTournament(tournamentId);
    }
  }
}

/**
 * Copy share link to clipboard (from publishing section)
 */
export async function copyPublishingShareLink(tournamentId: string, event?: Event): Promise<void> {
  const input = document.querySelector(`#publishing-section-${tournamentId} .share-link-input`) as HTMLInputElement;

  if (!input) return;

  try {
    await navigator.clipboard.writeText(input.value);

    // Visual feedback
    const button = event?.target as HTMLElement;
    if (button) {
      const originalText = button.textContent;
      button.textContent = t('common.copied', 'Copied!');
      setTimeout(() => {
        button.textContent = originalText || t('common.copy', 'Copy');
      }, 2000);
    }
  } catch (err) {
    // Fallback for older browsers
    input.select();
    document.execCommand('copy');

    const button = event?.target as HTMLElement;
    if (button) {
      const originalText = button.textContent;
      button.textContent = t('common.copied', 'Copied!');
      setTimeout(() => {
        button.textContent = originalText || t('common.copy', 'Copy');
      }, 2000);
    }
  }
}

/**
 * Show embed code modal
 */
export function showEmbedCode(tournamentId: string): void {
  const input = document.querySelector(`#publishing-section-${tournamentId} .share-link-input`) as HTMLInputElement;

  if (!input || !input.value) {
    alert(t('tournament.noShareLink', 'No share link available'));
    return;
  }

  const shareLink = input.value;
  const embedCode = `<iframe src="${shareLink}" width="100%" height="800" frameborder="0"></iframe>`;

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.style.display = 'block';
  modal.innerHTML = `
    <div class="modal-content">
      <div class="modal-header">
        <h2>${t('tournament.embedCode', 'Embed Code')}</h2>
        <span class="close" onclick="this.closest('.modal').remove()">&times;</span>
      </div>
      <div class="modal-body">
        <p>${t('tournament.embedCodeInstructions', 'Copy this code to embed the tournament on your website:')}</p>
        <textarea readonly class="embed-code-textarea" style="width: 100%; height: 100px; font-family: monospace; padding: 8px;"></textarea>
        <button class="btn btn-primary embed-copy-btn">
          ${t('common.copy', 'Copy')}
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  // Set textarea value and add event listener
  const textarea = modal.querySelector('.embed-code-textarea') as HTMLTextAreaElement;
  const copyBtn = modal.querySelector('.embed-copy-btn') as HTMLButtonElement;

  if (textarea) {
    textarea.value = embedCode;
  }

  if (copyBtn) {
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(embedCode);
        copyBtn.textContent = t('common.copied', 'Copied!');
        setTimeout(() => {
          copyBtn.textContent = t('common.copy', 'Copy');
        }, 2000);
      } catch (err) {
        // Fallback for older browsers
        textarea.select();
        document.execCommand('copy');
        copyBtn.textContent = t('common.copied', 'Copied!');
        setTimeout(() => {
          copyBtn.textContent = t('common.copy', 'Copy');
        }, 2000);
      }
    });
  }
}

/**
 * Toggle entry fees
 */
export async function toggleEntryFees(tournamentId: string, enabled: boolean): Promise<void> {
  const feeOptions = document.getElementById(`fee-options-${tournamentId}`);

  if (feeOptions) {
    feeOptions.style.display = enabled ? 'block' : 'none';
  }

  try {
    await updateTournamentAPI(tournamentId, { hasEntryFees: enabled });
  } catch (error) {
    console.error('Failed to toggle entry fees:', error);
    alert(t('common.error', 'Error') + ': ' + (error as Error).message);

    // Reset checkbox state on error
    const checkbox = document.getElementById(`has-fees-${tournamentId}`) as HTMLInputElement;
    if (checkbox) {
      checkbox.checked = !enabled;
    }
    if (feeOptions) {
      feeOptions.style.display = !enabled ? 'block' : 'none';
    }
  }
}

/**
 * Set payment mode for tournament
 * @param mode - 'onsite' (cash only), 'stripe' (online only), 'both' (player's choice)
 * @param event - Optional click event for immediate visual feedback
 */
export async function setPaymentMode(tournamentId: string, mode: 'onsite' | 'stripe' | 'both', event?: Event): Promise<void> {
  const stripeOptions = document.getElementById(`stripe-options-${tournamentId}`);
  const controlEl = document.getElementById(`payment-mode-${tournamentId}`);
  const buttons = controlEl?.querySelectorAll('.segment-button');

  // Find the previously active button to restore on error
  let previousMode: string | null = null;
  buttons?.forEach(btn => {
    if (btn.classList.contains('active')) {
      previousMode = btn.getAttribute('data-value');
    }
  });

  // Immediately update visual state for responsiveness
  buttons?.forEach(btn => {
    btn.classList.toggle('active', btn.getAttribute('data-value') === mode);
  });

  try {
    if (mode === 'onsite') {
      // Cash only - disconnect Stripe if connected
      if (window.disconnectStripeFromTournament) {
        try {
          await window.disconnectStripeFromTournament(tournamentId);
        } catch {
          // Ignore if not connected
        }
      }
      await updateTournamentAPI(tournamentId, { allowOnsitePayment: true });

      if (stripeOptions) {
        stripeOptions.style.display = 'none';
      }
    } else {
      // Stripe or both - need Stripe connected
      if (window.connectStripeToTournament) {
        await window.connectStripeToTournament(tournamentId);
      }
      await updateTournamentAPI(tournamentId, { allowOnsitePayment: mode === 'both' });

      if (stripeOptions) {
        stripeOptions.style.display = 'block';
      }

      // Initialize/refresh Stripe Connect UI
      if (window.initStripeConnectUI) {
        await window.initStripeConnectUI(tournamentId);
      }
    }
  } catch (error) {
    console.error('Failed to set payment mode:', error);
    // Restore previous button state on error
    if (previousMode) {
      buttons?.forEach(btn => {
        btn.classList.toggle('active', btn.getAttribute('data-value') === previousMode);
      });
    }
    // Show error to user
    const message = error instanceof Error ? error.message : 'Failed to set payment mode';
    if (typeof window.showNotification === 'function') {
      window.showNotification(message, 'error');
    } else {
      alert('Error: ' + message);
    }
  }
}

/**
 * Toggle Stripe payments (legacy - kept for backward compatibility)
 */
export async function toggleStripe(tournamentId: string, enabled: boolean): Promise<void> {
  await setPaymentMode(tournamentId, enabled ? 'stripe' : 'onsite');
}

/**
 * Open Stripe admin - redirects to Stripe dashboard
 */
export function openStripeAdmin(tournamentId: string): void {
  if (window.openStripeDashboard) {
    window.openStripeDashboard();
  } else {
    alert(t('tournament.stripeAdminComingSoon', 'Stripe administration coming soon!'));
  }
}

/**
 * Edit description with rich text editor (Quill)
 */
export function editDescription(tournamentId: string, event?: Event): void {
  const descriptionContent = event ? (event.target as HTMLElement)?.closest('.description-content') : document.querySelector(`#overview-${tournamentId} .description-content`);

  if (!descriptionContent) return;

  // Prevent multiple edits
  if (descriptionContent.classList.contains('editing')) return;
  descriptionContent.classList.add('editing');

  // Expand the wrapper if collapsed to ensure we're working with full content
  const wrapper = descriptionContent.querySelector('.description-wrapper') as HTMLElement;
  if (wrapper && !wrapper.classList.contains('expanded')) {
    wrapper.classList.add('expanded');
  }

  // Get current description HTML (not just text)
  const descriptionTextEl = descriptionContent.querySelector('.description-text') as HTMLElement;
  const isPlaceholder = descriptionTextEl?.classList.contains('empty');
  const currentHTML = isPlaceholder ? '' : (descriptionTextEl?.innerHTML || '');

  const originalHTML = descriptionContent.innerHTML;

  descriptionContent.innerHTML = `
    <div id="description-editor-${tournamentId}" style="background: white; border: 1px solid #ddd; border-radius: 6px; min-height: 200px;"></div>
    <div style="display: flex; gap: 8px; margin-top: 12px;">
      <button class="btn-save-description btn btn-primary btn-small">${t('common.save', 'Save')}</button>
      <button class="btn-cancel-description btn btn-secondary btn-small">${t('common.cancel', 'Cancel')}</button>
    </div>
  `;

  const editorContainer = descriptionContent.querySelector(`#description-editor-${tournamentId}`) as HTMLElement;
  const saveBtn = descriptionContent.querySelector('.btn-save-description') as HTMLButtonElement;
  const cancelBtn = descriptionContent.querySelector('.btn-cancel-description') as HTMLButtonElement;

  if (!editorContainer || !saveBtn || !cancelBtn) {
    descriptionContent.innerHTML = originalHTML;
    descriptionContent.classList.remove('editing');
    return;
  }

  // Initialize Quill editor
  const quill = new Quill(editorContainer, {
    theme: 'snow',
    modules: {
      toolbar: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'color': [] }, { 'background': [] }],
        ['link'],
        ['clean']
      ]
    },
    placeholder: t('tournament.addDescription', 'Click to add a description...')
  });

  // Set initial content using clipboard for proper HTML parsing
  if (currentHTML) {
    quill.clipboard.dangerouslyPasteHTML(currentHTML);
  }

  // Focus the editor
  quill.focus();

  // Cancel handler
  const cancel = () => {
    descriptionContent.innerHTML = originalHTML;
    descriptionContent.classList.remove('editing');
  };

  cancelBtn.addEventListener('click', cancel);

  // Save handler
  const save = async () => {
    const newValue = quill.root.innerHTML.trim();

    saveBtn.disabled = true;
    cancelBtn.disabled = true;
    saveBtn.textContent = t('common.saving', 'Saving...');

    try {
      await updateTournamentAPI(tournamentId, { description: newValue });

      if (window.loadSingleTournament) {
        await window.loadSingleTournament(tournamentId);
      }
    } catch (error) {
      console.error('Failed to update description:', error);
      alert(t('common.error', 'Error') + ': ' + (error as Error).message);
      cancel();
    }
  };

  saveBtn.addEventListener('click', save);
}

// Export to window
window.toggleFeatureSection = toggleFeatureSection;
window.autoOpenSections = autoOpenSections;
window.toggleSelfRegistration = toggleSelfRegistration;
window.togglePlayerResultEntry = togglePlayerResultEntry;
window.toggleShareLinkVisibility = toggleShareLinkVisibility;
window.togglePublicDirectory = togglePublicDirectory;
window.copyPublishingShareLink = copyPublishingShareLink;
window.showEmbedCode = showEmbedCode;
window.toggleEntryFees = toggleEntryFees;
window.toggleStripe = toggleStripe;
window.setPaymentMode = setPaymentMode;
window.openStripeAdmin = openStripeAdmin;
window.editDescription = editDescription;
